import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SelectChangeEvent } from '@mui/material/Select';

import { headerActions } from 'app/pages/Header/slice';
import { selectSelectedAgentType } from 'app/pages/Header/selectors';
import {
  AGENT_TYPE_CHANGE_EVENT,
  AgentTypeValue,
  getAgentType,
  setAgentType,
} from 'app/utils/agentType';
import queryClient from 'app/apiService/queryClient';
import { AGENT_TYPE_NONE_VALUE } from './constants';

/**
 * Owns all logic/state for the AgentTypeDropdown.
 *
 * Responsibilities:
 *   - Read the current selection (Redux is source of truth for the UI).
 *   - Persist user changes to localStorage (so the HTTP layer can read it).
 *   - Invalidate the React Query cache on change so every page refetches
 *     with the new `agent_type` query param applied.
 *   - Stay in sync with cross-tab changes via `storage` event.
 *   - On selection change, do a full page reload so every saga / hook /
 *     useEffect across the app re-runs with the new filter, mirroring the
 *     "everything refreshes" UX of the dealer dropdown.
 */
export const useAgentTypeDropdown = () => {
  const dispatch = useDispatch();
  const selectedAgentType = useSelector(selectSelectedAgentType);

  // Keep Redux in sync if localStorage was already populated before
  // the slice initialised (e.g. dropdown mounted from a different layout).
  React.useEffect(() => {
    const storedValue = getAgentType();
    if ((storedValue || null) !== (selectedAgentType || null)) {
      dispatch(headerActions.setSelectedAgentType(storedValue));
    }
    // Intentionally run once on mount.
  }, []);

  // Cross-tab + intra-app sync: react to setAgentType() from any context.
  React.useEffect(() => {
    const handleChange = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const nextValue: string | null = detail?.value ?? null;
      dispatch(headerActions.setSelectedAgentType(nextValue));
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== 'selected_agent_type') return;
      dispatch(headerActions.setSelectedAgentType(getAgentType()));
    };

    window.addEventListener(AGENT_TYPE_CHANGE_EVENT, handleChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(AGENT_TYPE_CHANGE_EVENT, handleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [dispatch]);

  const handleChange = React.useCallback(
    (event: SelectChangeEvent<string>) => {
      const rawValue = event.target.value;
      const nextValue: AgentTypeValue | null =
        rawValue && rawValue !== AGENT_TYPE_NONE_VALUE
          ? (rawValue as AgentTypeValue)
          : null;

      // Skip work (and the reload) if the user re-picked the same value.
      const currentValue = getAgentType();
      if ((currentValue || null) === (nextValue || null)) {
        return;
      }

      // 1) Persist + broadcast (HTTP layer reads from here).
      setAgentType(nextValue);

      // 2) Keep Redux in sync for any UI subscribers.
      dispatch(headerActions.setSelectedAgentType(nextValue));

      // 3) Drop every cached query so pages refetch with the new filter.
      try {
        queryClient.removeQueries();
      } catch (error) {
        console.error('AgentTypeDropdown cache invalidation error:', error);
      }

      // 4) Hard reload the current page so every saga, useEffect, and data
      //    fetch re-runs with `agent_type` injected by the HTTP layer.
      //    This mirrors the "everything refreshes" UX of the dealer dropdown
      //    in one shot, without having to wire `selectedAgentType` into
      //    every page's effect dependencies.
      try {
        window.location.reload();
      } catch (error) {
        console.error('AgentTypeDropdown reload error:', error);
      }
    },
    [dispatch],
  );

  // MUI Select treats `null` as uncontrolled; coerce to empty string.
  const value: string = selectedAgentType ?? AGENT_TYPE_NONE_VALUE;

  return {
    value,
    handleChange,
  };
};
